import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  Stack,
  Spinner,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import useRegions from 'hooks/useRegions';
import { checkNameConflict, getScopeImpact } from 'utils/labelApi';
import { GLOBAL_LABEL_SCOPE_REGION_ID } from 'utils/StaticData';
import LabelBadge from './LabelBadge';

// Change a label's scope (RAT-1687). Two directions:
//  - Upgrade (single region -> Global): if the name clashes with another
//    label, the user chooses merge vs keep-both. Otherwise a plain convert.
//  - Downgrade (Global -> single region): shows how many assigned merchants
//    outside the target region will lose the label, and requires
//    confirmation before any association is removed.
const LabelScopeModal = ({
  isOpen,
  onClose,
  label,
  onConfirm,
  isSaving,
  initialTargetRegionId,
}) => {
  const { regions } = useRegions();
  const isGlobal = label?.regionId === GLOBAL_LABEL_SCOPE_REGION_ID;
  const firstRegionId = regions[0]?.id;

  const [targetRegionId, setTargetRegionId] = useState(
    initialTargetRegionId ?? firstRegionId,
  );
  const [resolution, setResolution] = useState('convert');

  const regionName = (id) =>
    id === GLOBAL_LABEL_SCOPE_REGION_ID
      ? 'Global'
      : regions.find((r) => r.id === id)?.label || id;

  // Any-scope conflict, for the upgrade path (region -> Global) - decides
  // whether to offer merge/keep-both. Checking against the Global scope
  // covers "any scope" per the per-scope uniqueness rule.
  const [conflict, setConflict] = useState(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);

  useEffect(() => {
    if (!isOpen || !label || isGlobal) {
      setConflict(null);
      return;
    }
    let cancelled = false;
    setIsCheckingConflict(true);
    checkNameConflict(label.name, GLOBAL_LABEL_SCOPE_REGION_ID, label.id)
      .then((result) => {
        if (!cancelled) setConflict(result);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingConflict(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, label, isGlobal]);

  useEffect(() => {
    if (isOpen) {
      setTargetRegionId(initialTargetRegionId ?? firstRegionId);
      setResolution(conflict ? 'merge' : 'convert');
    }
  }, [isOpen, conflict, firstRegionId, initialTargetRegionId]);

  // Impact only applies to a downgrade (Global -> single region): how many
  // assigned merchants sit outside the target region and would lose the label.
  const [impact, setImpact] = useState({ total: 0, byRegion: {} });
  const [isCheckingImpact, setIsCheckingImpact] = useState(false);

  useEffect(() => {
    if (!isOpen || !label || !isGlobal || !targetRegionId) {
      setImpact({ total: 0, byRegion: {} });
      return;
    }
    let cancelled = false;
    setIsCheckingImpact(true);
    getScopeImpact(label.id, targetRegionId)
      .then((result) => {
        if (!cancelled) setImpact(result);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingImpact(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, label, isGlobal, targetRegionId]);

  // Downgrade (Global -> region) must not create a duplicate name in the
  // target region (RAT-1687). If it would, block the action.
  const [downgradeNameClash, setDowngradeNameClash] = useState(null);

  useEffect(() => {
    if (!isOpen || !label || !isGlobal || !targetRegionId) {
      setDowngradeNameClash(null);
      return;
    }
    let cancelled = false;
    checkNameConflict(label.name, targetRegionId, label.id).then((result) => {
      if (!cancelled) setDowngradeNameClash(result);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, label, isGlobal, targetRegionId]);

  const isCheckingAnything = isCheckingConflict || isCheckingImpact;

  if (!label) return null;

  const handleConfirm = () => {
    if (downgradeNameClash) return;
    if (isGlobal) {
      onConfirm({ targetRegionId, resolution: null });
    } else {
      onConfirm({
        targetRegionId: GLOBAL_LABEL_SCOPE_REGION_ID,
        resolution: conflict ? resolution : undefined,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent w={{ base: 'calc(100% - 24px)', md: '100%' }}>
        <ModalHeader>
          {isGlobal ? 'Downgrade from Global' : 'Upgrade to Global'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          <HStack mb="4" spacing="2" wrap="wrap">
            <LabelBadge label={label} />
            <Text color="gray.500">
              {regionName(label.regionId)} {'->'}{' '}
              {isGlobal ? 'single region' : 'Global'}
            </Text>
            {isCheckingAnything && <Spinner size="sm" />}
          </HStack>

          <VStack spacing="4" align="stretch">
            {isGlobal ? (
              <>
                <FormControl isRequired>
                  <FormLabel
                    htmlFor="scope-target"
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    Target region
                  </FormLabel>
                  <Select
                    id="scope-target"
                    value={targetRegionId}
                    onChange={(e) => setTargetRegionId(Number(e.target.value))}
                    borderRadius="8px"
                  >
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {downgradeNameClash && (
                  <Alert status="error" borderRadius="10px">
                    <AlertIcon />
                    <Text fontSize="0.85rem">
                      {regionName(targetRegionId)} already has a label named
                      &quot;
                      {downgradeNameClash.name}&quot;. Rename one of them before
                      downgrading - names must be unique within a scope.
                    </Text>
                  </Alert>
                )}
                {impact.total > 0 ? (
                  <Alert
                    status="error"
                    borderRadius="10px"
                    alignItems="flex-start"
                  >
                    <AlertIcon />
                    <Box fontSize="0.85rem">
                      <Text fontWeight="600" mb="1">
                        {impact.total} merchant(s) outside{' '}
                        {regionName(targetRegionId)} will lose this label.
                      </Text>
                      <Text mb="2">
                        The label is removed from them in the same action. This
                        can&apos;t be undone.
                      </Text>
                      {Object.entries(impact.byRegion).map(([name, n]) => (
                        <HStack key={name} justify="space-between" maxW="220px">
                          <Text>{name}</Text>
                          <Text fontWeight="600">
                            {n} merchant{n === 1 ? '' : 's'}
                          </Text>
                        </HStack>
                      ))}
                      <Text mt="2" color="gray.600">
                        Splitting these into their own labels is out of scope -
                        ops can recreate them later if needed.
                      </Text>
                    </Box>
                  </Alert>
                ) : (
                  <Alert status="info" borderRadius="10px">
                    <AlertIcon />
                    <Text fontSize="0.85rem">
                      No merchants affected - every merchant carrying this label
                      is already in {regionName(targetRegionId)}.
                    </Text>
                  </Alert>
                )}
              </>
            ) : conflict ? (
              <>
                <Alert status="warning" borderRadius="10px">
                  <AlertIcon />
                  <Text fontSize="0.85rem">
                    A label named &quot;{label.name}&quot; already exists (
                    {conflict.regionName}). Choose how to resolve the clash when
                    going Global.
                  </Text>
                </Alert>
                <RadioGroup value={resolution} onChange={setResolution}>
                  <Stack spacing="3">
                    <Radio value="merge" alignItems="flex-start">
                      <Box>
                        <Text fontWeight="600" fontSize="sm">
                          Merge into the existing label
                        </Text>
                        <Text fontSize="0.8rem" color="gray.600">
                          Move this label&apos;s merchant assignments onto
                          &quot;{conflict.name}
                          &quot;, then remove this one. Assignments combine.
                        </Text>
                      </Box>
                    </Radio>
                    <Radio value="keepBoth" alignItems="flex-start">
                      <Box>
                        <Text fontWeight="600" fontSize="sm">
                          Keep both separate
                        </Text>
                        <Text fontSize="0.8rem" color="gray.600">
                          Convert to Global but auto-suffix this one to &quot;
                          {label.name} (2) &quot; so the two stay distinct.
                        </Text>
                      </Box>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </>
            ) : (
              <Alert status="info" borderRadius="10px">
                <AlertIcon />
                <Text fontSize="0.85rem">
                  No naming conflict. The label becomes Global and keeps all its
                  current merchant assignments. No merchants are affected.
                </Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={onClose}
            mr="3"
            borderRadius="12px"
            fontSize={{ base: '12px', md: '14px' }}
            fontWeight="500"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            // A downgrade that strips merchants stays red - it is destructive.
            // Everything else uses the app's standard primary CTA styling.
            {...(isGlobal && impact.total > 0
              ? { colorScheme: 'red' }
              : {
                  bg: 'reviveOrange',
                  color: 'primary',
                  _hover: { bg: 'reviveOrange', opacity: 0.8 },
                })}
            borderRadius="12px"
            fontSize={{ base: '12px', md: '14px' }}
            fontWeight="500"
            onClick={handleConfirm}
            isLoading={isSaving}
            isDisabled={Boolean(downgradeNameClash) || isCheckingAnything}
            loadingText="Applying..."
          >
            {isGlobal ? 'Confirm downgrade' : 'Convert to Global'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

LabelScopeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  initialTargetRegionId: PropTypes.number,
};

export default LabelScopeModal;
